<template>
	<v-container fluid class="veiculo-page pa-8 pt-12">
		<div class="veiculo-header">
			<div>
				<h1 class="veiculo-title">Veículos</h1>
				<p class="veiculo-subtitle">
					Controle os veículos da sua transportadora, gerencie informações, documentos e status.
				</p>
			</div>
			<div class="veiculo-header__actions">
				<v-badge
					:content="documentacaoBadgeContent()"
					:value="documentosVencidosCount > 0"
					color="#ef4444"
					overlap
					bordered
				>
					<v-btn class="veiculo-new-btn mr-2" outlined :disabled="!veiculos.length" @click="irDocumentacao()">
						<v-icon left size="18">mdi-file-document-outline</v-icon>
						Documentação
					</v-btn>
				</v-badge>
				<v-btn class="veiculo-new-btn" depressed @click="novoVeiculo()">
					<v-icon left size="18">mdi-plus</v-icon>
					Novo Veículo
				</v-btn>
			</div>
		</div>

		<!-- Tabela -->
		<v-card class="veiculo-table-card mt-6" outlined>
			<v-data-table
				class="veiculo-table"
				:headers="headers"
				:items="veiculos"
				:loading="loading"
				loading-text="Carregando veículos..."
				no-data-text="Nenhum veículo cadastrado"
				no-results-text="Nenhum resultado encontrado. Por favor, tente outra pesquisa."
				:items-per-page="8"
				:footer-props="{
					itemsPerPageText: 'Itens por Página',
					itemsPerPageOptions: [10, 15, 20],
					pageText: '{0}-{1} de {2}'
				}"
			>
				<template v-slot:[`item.tipo`]="{ item }">
					<div class="veiculo-tipo-cell">
						<v-icon size="18" color="#10b981" class="mr-2">{{ getTipoIcon(item.tipo) }}</v-icon>
						<span>{{ item.tipo || '-' }}</span>
					</div>
				</template>
				<template v-slot:[`item.conjunto`]="{ item }">
					<div class="veiculo-conjunto-cell">
						<span class="veiculo-conjunto-placas">{{ formatConjunto(item) }}</span>
						<v-tooltip v-if="hasConjuntoError(item.id)" top>
							<template v-slot:activator="{ on, attrs }">
								<v-icon
									size="18"
									color="#f59e0b"
									class="ml-1"
									v-bind="attrs"
									v-on="on"
								>
									mdi-alert-circle
								</v-icon>
							</template>
							<span>{{ getConjuntoError(item.id) }}</span>
						</v-tooltip>
						<v-btn
							v-if="isTipoBase(item.tipo)"
							x-small
							icon
							class="ml-2"
							@click="abrirDialogConjunto(item)"
						>
							<v-icon size="18" color="#10b981">mdi-pencil</v-icon>
						</v-btn>
					</div>
				</template>
				<template v-slot:[`item.status`]="{ item }">
					<span class="veiculo-status" :class="isStatusAtivo(item.status) ? 'is-active' : 'error--text'">
						{{ formatStatus(item.status) }}
					</span>
				</template>
				<template v-slot:[`item.actions`]="{ item }">
					<v-menu offset-y left>
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								icon
								small
								class="veiculo-actions"
								v-bind="attrs"
								v-on="on"
							>
								<v-icon color="#10b981">mdi-dots-vertical</v-icon>
							</v-btn>
						</template>
						<v-list dense class="veiculo-actions__menu">
							<v-list-item @click="editarVeiculo(item)">
								<v-list-item-icon>
									<v-icon color="#10b981" size="18">mdi-pencil-outline</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Editar</v-list-item-title>
							</v-list-item>
							<v-divider />
							<v-list-item @click="abrirDetalhesVeiculo(item)">
								<v-list-item-icon>
									<v-icon color="#10b981" size="18">mdi-clipboard-text-search-outline</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Dados do Veiculo</v-list-item-title>
							</v-list-item>
							<v-list-item @click="abrirDialogStatus(item)">
								<v-list-item-icon>
									<v-icon color="#10b981" size="18">mdi-ballot-recount</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Mudar status</v-list-item-title>
							</v-list-item>
							<v-list-item @click="irMapaPneus(item)">
								<v-list-item-icon>
									<v-icon color="#333333" size="18">mdi-tire</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Mapa de Pneus</v-list-item-title>
							</v-list-item>
							<v-divider />
							<v-list-item @click="abrirExclusao(item)">
								<v-list-item-icon>
									<v-icon color="#ef4444" size="18">mdi-delete-outline</v-icon>
								</v-list-item-icon>
								<v-list-item-title>Excluir</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>
				</template>
			</v-data-table>
		</v-card>

		<v-dialog v-model="conjuntoDialogOpen" max-width="620" persistent>
			<v-card>
				<v-card-title class="veiculo-dialog__title">
					<span>Vincular Carretas</span>
					<v-spacer />
					<v-btn icon :disabled="conjuntoSaving" @click="conjuntoDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-5">
					<div class="mb-3">
						<strong>Veículo base:</strong>
						{{ conjuntoVeiculo?.placa || '-' }}
						({{ conjuntoVeiculo?.tipo || '-' }})
					</div>

					<v-autocomplete
						v-model="conjuntoSelecionadoIds"
						:items="carretasDisponiveis"
						item-text="placa"
						item-value="id"
						multiple
						chips
						small-chips
						outlined
						dense
						clearable
						:loading="conjuntoLoading"
						label="Selecione as carretas vinculadas"
						no-data-text="Nenhuma carreta disponível"
					/>
				</v-card-text>
				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text :disabled="conjuntoSaving" @click="conjuntoDialogOpen = false">Cancelar</v-btn>
					<v-btn class="veiculo-new-btn" :loading="conjuntoSaving" :disabled="conjuntoSaving" depressed @click="salvarConjunto">Salvar</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog Detalhes -->
		<v-dialog v-model="detalhesDialogOpen" max-width="600" scrollable>
			<v-card class="veiculo-dialog" outlined>
				<v-card-title class="veiculo-dialog__title">
					<span>Detalhes do Veículo</span>
					<v-spacer />
					<v-btn icon @click="detalhesDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-4">
					<v-container class="pa-0" fluid>
						<v-row dense>
							<v-col cols="12" md="6"><strong>Placa:</strong> {{ veiculoDetalhes?.placa || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Marca:</strong> {{ veiculoDetalhes?.marca || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Modelo:</strong> {{ veiculoDetalhes?.modelo || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Ano:</strong> {{ veiculoDetalhes?.ano || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Cor:</strong> {{ veiculoDetalhes?.cor || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Tipo:</strong> {{ veiculoDetalhes?.tipo || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Status:</strong> {{ veiculoDetalhes?.status || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Chassi:</strong> {{ veiculoDetalhes?.chassi || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Renavam:</strong> {{ veiculoDetalhes?.renavam || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>KM na Aquisição:</strong> {{ veiculoDetalhes?.quilometragem_atual || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Data de Aquisição:</strong> {{ veiculoDetalhes?.data_aquisicao || '-' }}</v-col>
							<v-col cols="12" md="6"><strong>Data de Venda:</strong> {{ veiculoDetalhes?.data_venda || '-' }}</v-col>
						</v-row>
					</v-container>
				</v-card-text>
				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text @click="detalhesDialogOpen = false">Fechar</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="statusDialogOpen" max-width="320" persistent>
			<v-card>
				<v-card-title class="veiculo-dialog__title">
					<span>Mudar status</span>
					<v-spacer />
					<v-btn icon :disabled="statusSaving" @click="statusDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-2">
					<v-row dense>
						<v-col cols="12">
							<v-select
								label="Status"
								:items="statusVeiculo"
								v-model="veiculoStatusEdicao.status"
								outlined
								dense
							/>
						</v-col>
					</v-row>
				</v-card-text>
				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text :disabled="statusSaving" @click="statusDialogOpen = false">Cancelar</v-btn>
					<v-btn class="veiculo-new-btn" depressed :loading="statusSaving" :disabled="statusSaving" @click="salvarStatusVeiculo">
						Salvar
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="deleteDialogOpen" max-width="420" persistent>
			<v-card class="veiculo-dialog" outlined>
				<v-card-title class="veiculo-dialog__title">
					<span>Excluir Veiculo</span>
					<v-spacer />
					<v-btn icon :disabled="deleting" @click="deleteDialogOpen = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pt-6 pb-2">
					<div class="veiculo-confirmacao__texto">
						Tem certeza que deseja excluir o veiculo <strong>{{ veiculoExclusao.placa || '-' }}</strong>?
					</div>
					<div class="veiculo-confirmacao__aviso">
						Esta acao nao pode ser desfeita. Veiculos com historico vinculado devem ter o status alterado.
					</div>
				</v-card-text>
				<v-card-actions class="px-6 pb-4">
					<v-spacer />
					<v-btn text :disabled="deleting" @click="deleteDialogOpen = false">Cancelar</v-btn>
					<v-btn class="veiculo-delete-btn" depressed :loading="deleting" :disabled="deleting" @click="confirmarExclusao">
						Excluir
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
export default {
	name: 'ListagemVeiculos',
	data() {
		return {
			loading: false,
			veiculos: [],
			conjuntoDialogOpen: false,
			conjuntoLoading: false,
			conjuntoSaving: false,
			conjuntoVeiculo: null,
			conjuntoSelecionadoIds: [],
			carretasDisponiveis: [],
			conjuntoErrosMap: {},
			statusDialogOpen: false,
			statusSaving: false,
			deleteDialogOpen: false,
			deleting: false,
			veiculoExclusao: {},
			statusVeiculo: ['Ativo', 'Vendido', 'Baixado', 'Manutenção'],
			veiculoStatusEdicao: {
				id: null,
				status: null,
			},
			documentosVencidosCount: 0,
			headers: [
				{ text: 'Placa', value: 'placa' },
				{ text: 'Tipo', value: 'tipo' },
				{ text: 'Conjunto', value: 'conjunto' },
				{ text: 'Status', value: 'status' },
				{ text: 'Ações', value: 'actions', sortable: false },
			],
			detalhesDialogOpen: false,
			veiculoDetalhes: null,
		};
	},
	methods: {
		documentacaoBadgeContent() {
			if (this.documentosVencidosCount > 99) {
				return '99+';
			}

			return this.documentosVencidosCount;
		},
		async fetchVeiculos() {
			this.loading = true;
			try {
				const veiculoServices = (await import('./veiculoServices.js')).default;
				const response = await veiculoServices.listar();
				this.veiculos = Array.isArray(response.data) ? response.data : [];
			} catch (e) {
				this.veiculos = [];
			}
			this.loading = false;
		},
		async fetchDocumentosVencidos() {
			try {
				const documentacaoServices = (await import('./documentacaoServices.js')).default;
				const response = await documentacaoServices.verificarDocumentoVencido();
				this.documentosVencidosCount = Array.isArray(response) ? response.length : 0;
			} catch (error) {
				this.documentosVencidosCount = 0;
			}
		},
		novoVeiculo() {
			// Navegar para tela de cadastro
			this.$router.push({ name: 'CadastroVeiculo' });
		},
		editarVeiculo(item) {
			this.$router.push({
				name: 'EditarVeiculo',
				params: {
					id: item.id,
					veiculo: item,
				},
			});
		},
		irDocumentacao() {
			this.$router.push({ name: 'DocumentacaoVeiculo' });
		},
		irMapaPneus(item) {
			if (item && item.id) {
				this.$router.push({ name: 'MapaPneusVeiculo', params: { id: item.id } });
			}
		},
		normalizeTipo(tipo) {
			return String(tipo || '')
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.trim()
				.toUpperCase();
		},
		isTipoBase(tipo) {
			const key = this.normalizeTipo(tipo);
			return key === 'TOCO' || key === 'TRUCK';
		},
		formatConjunto(item) {
			if (!this.isTipoBase(item?.tipo)) {
				return '-';
			}

			const text = String(item?.conjunto || '').trim();
			return text || '-';
		},
		hasConjuntoError(veiculoId) {
			return Boolean(this.conjuntoErrosMap[String(veiculoId)]);
		},
		getConjuntoError(veiculoId) {
			return this.conjuntoErrosMap[String(veiculoId)] || 'Erro no vínculo do conjunto.';
		},
		clearConjuntoError(veiculoId) {
			if (!veiculoId) return;
			this.$delete(this.conjuntoErrosMap, String(veiculoId));
		},
		async abrirDialogConjunto(item) {
			if (!item?.id) return;

			this.conjuntoDialogOpen = true;
			this.conjuntoLoading = true;
			this.conjuntoVeiculo = item;
			this.conjuntoSelecionadoIds = [];
			this.clearConjuntoError(item.id);

			try {
				const veiculoServices = (await import('./veiculoServices.js')).default;
				const [carretas, conjunto] = await Promise.all([
					veiculoServices.listarCarretasDisponiveis(),
					veiculoServices.buscarConjunto(item.id),
				]);

				this.carretasDisponiveis = Array.isArray(carretas) ? carretas : [];
				this.conjuntoSelecionadoIds = Array.isArray(conjunto?.carreta_ids)
					? conjunto.carreta_ids
					: [];
			} catch (error) {
				this.$toasted?.show(error?.response?.data?.error || 'Erro ao carregar vínculo de carretas.');
			} finally {
				this.conjuntoLoading = false;
			}
		},
		async salvarConjunto() {
			if (!this.conjuntoVeiculo?.id) return;

			this.conjuntoSaving = true;
			try {
				const veiculoServices = (await import('./veiculoServices.js')).default;
				await veiculoServices.salvarConjunto(this.conjuntoVeiculo.id, this.conjuntoSelecionadoIds);
				this.clearConjuntoError(this.conjuntoVeiculo.id);
				this.$toasted?.show('Vínculo de carretas salvo com sucesso.');
				this.conjuntoDialogOpen = false;
				await this.fetchVeiculos();
			} catch (error) {
				const msg = error?.response?.data?.error || 'Erro ao salvar vínculo de carretas.';
				this.$set(this.conjuntoErrosMap, String(this.conjuntoVeiculo.id), msg);
				this.$toasted?.show(msg);
			} finally {
				this.conjuntoSaving = false;
			}
		},
		isStatusAtivo(status) {
			const normalized = String(status || '').trim().toUpperCase();
			return normalized === 'ATIVO';
		},
		formatStatus(status) {
			const map = {
				ATIVO: 'Ativo',
				VENDIDO: 'Vendido',
				BAIXADO: 'Baixado',
				MANUTENCAO: 'Manutenção',
				'MANUTENÇÃO': 'Manutenção',
			};

			const normalized = String(status || '').trim().toUpperCase();
			return map[normalized] || status || '-';
		},
		getTipoIcon(tipo) {
			const key = String(tipo || '').trim().toUpperCase();
			const iconByType = {
				CARRO: 'mdi-car',
				MOTO: 'mdi-motorbike',
				VAN: 'mdi-van-passenger',
				ONIBUS: 'mdi-bus',
				'ÔNIBUS': 'mdi-bus',
				TOCO: 'mdi-truck-flatbed',
				TRUCK: 'mdi-truck-flatbed',
				CARRETA: 'mdi-truck-trailer',
			};

			return iconByType[key] || 'mdi-truck-outline';
		},
		abrirDetalhesVeiculo(item) {
			this.veiculoDetalhes = {
				...item,
				status: this.formatStatus(item?.status),
				quilometragem_atual: item?.quilometragem_atual ?? item?.quilometragem_aquisicao ?? null,
			};
			this.detalhesDialogOpen = true;
		},
		abrirDialogStatus(item) {
			if (!item?.id) return;

			this.veiculoStatusEdicao = {
				id: item.id,
				status: String(item.status || 'Ativo'),
			};
			this.statusDialogOpen = true;
		},
		abrirExclusao(item) {
			this.veiculoExclusao = item || {};
			this.deleteDialogOpen = true;
		},
		async salvarStatusVeiculo() {
			if (!this.veiculoStatusEdicao?.id || !this.veiculoStatusEdicao?.status) return;

			this.statusSaving = true;
			try {
				const veiculoServices = (await import('./veiculoServices.js')).default;
				await veiculoServices.atualizarStatus(this.veiculoStatusEdicao.id, this.veiculoStatusEdicao.status);

				this.$toasted?.show('Status do veículo atualizado com sucesso.');
				this.statusDialogOpen = false;
				await this.fetchVeiculos();
				await this.fetchDocumentosVencidos();
			} catch (error) {
				this.$toasted?.show(error?.response?.data?.error || 'Erro ao atualizar status do veículo.');
			} finally {
				this.statusSaving = false;
			}
		},
		async confirmarExclusao() {
			if (!this.veiculoExclusao?.id) return;

			this.deleting = true;
			try {
				const veiculoServices = (await import('./veiculoServices.js')).default;
				await veiculoServices.excluir(this.veiculoExclusao.id);

				this.$toasted?.show('Veiculo excluido com sucesso.');
				this.deleteDialogOpen = false;
				this.veiculoExclusao = {};
				await this.fetchVeiculos();
				await this.fetchDocumentosVencidos();
			} catch (error) {
				this.$toasted?.show(error?.response?.data?.error || 'Erro ao excluir veiculo.');
			} finally {
				this.deleting = false;
			}
		},
	},
	mounted() {
		this.fetchVeiculos();
		this.fetchDocumentosVencidos();
	},
};
</script>

<style scoped>
@import "../../assets/inter-font.css";

.veiculo-page {
	font-family: "Inter", sans-serif;
}

/* ...existing code... */
.veiculo-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 16px;
}

.veiculo-header > div:first-child {
	flex: 1;
}

.veiculo-header__actions {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
}

.veiculo-header__actions {
	display: flex;
	align-items: center;
	gap: 8px;
}

.veiculo-title {
	color: #10b981;
	font-size: 22px;
	font-weight: 800;
	line-height: 1.2;
	margin: 0;
}

.veiculo-subtitle {
	color: #7a8ca6;
	font-size: 13px;
	margin: 6px 0 0;
}

.veiculo-new-btn {
	border-radius: 12px;
	text-transform: none;
	font-weight: 700;
	letter-spacing: 0.01em;
	padding: 10px 16px;
}

.veiculo-new-btn:not(.v-btn--outlined) {
	background-color: #10b981 !important;
	color: #ffffff !important;
}

.veiculo-new-btn.v-btn--outlined {
	background-color: #fff !important;
	color: #10b981 !important;
	border: 2px solid #10b981 !important;
}

.veiculo-table-card {
	border-radius: 16px;
	border-color: #e6ebf1 !important;
}

.veiculo-table {
	border-radius: 16px;
}

.veiculo-table ::v-deep th {
	color: #7a8ca6 !important;
	font-weight: 800 !important;
	font-size: 11px !important;
	letter-spacing: 0.08em;
}

.veiculo-table ::v-deep td {
	color: #1f2a44;
	font-size: 13px;
}

.veiculo-table ::v-deep .v-data-table__wrapper {
	border-radius: 16px;
}

.veiculo-status {
	font-weight: 800;
}

.veiculo-tipo-cell {
	display: flex;
	align-items: center;
}

.veiculo-conjunto-cell {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 6px;
}

.veiculo-conjunto-placas {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 180px;
}

/* ...existing code... */
.veiculo-status.is-active {
	color: #10b981;
}
.veiculo-status.error--text {
	color: #841f1f;
}

.veiculo-actions {
	opacity: 0.9;
}

.veiculo-actions__menu {
	min-width: 180px;
}

.veiculo-delete-btn {
	background-color: #ef4444 !important;
	color: #ffffff !important;
	border-radius: 12px;
	text-transform: none;
	font-weight: 800;
}

.veiculo-confirmacao__texto {
	color: #1f2a44;
	font-size: 14px;
	line-height: 1.6;
}

.veiculo-confirmacao__aviso {
	color: #7a8ca6;
	font-size: 13px;
	margin-top: 10px;
}
</style>
